import React, { useEffect, useMemo, useState } from 'react';
import { deleteUserInventoryMapping, fetchAllUserInventoryMapping } from 'services/api/userInventoryMapping';
import { Loading } from 'components/loading/Loading';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import DataTable from 'components/DataTable';
import { showError, showSuccess } from 'utils';
import { tableStyles } from 'css/tableStyles';
import CustomLink from 'components/TableLink';
import AddUserInventoryItemModal from 'components/AddUserInventoryItemModal';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import EditActionForTable from 'components/EditActionForTableData';

const supplierMappingDefaultState = { id:'', originalDiscountPercent : 0, originalFixedDiscountAmount : 0 };
const UsersInventory = props => {
  const { userId } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalUpdateOpen, setModalUpdateOpen] = useState<boolean>(false);
  const [itemSelected, setItemSelected] = useState<string>('');
  const [selectedUserMapping,setSelectedUserMapping] = useState<Record<string,any>>(supplierMappingDefaultState);


  const fetchUsersInventoryFn = async () => {
    try {
      setLoading(true);
      const response = await fetchAllUserInventoryMapping({
        userId,
      });
      setData(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsersInventoryFn();
  }, []);

  const deleteUserInventoryMappingFn = async (id: string) => {
    try {
      await deleteUserInventoryMapping(id);
      await fetchUsersInventoryFn();
      showSuccess('Inventory Item removed successfully.');
    } catch (e) {
      showError(e);
    }
  };

  const editUserInventoryMappingFn = (row) => {
    setItemSelected(row.inventoryItem.objectId);
    setSelectedUserMapping({
      id:row.objectId,
      originalDiscountPercent: row.discountPercent,
      originalFixedDiscountAmount: row.fixedDiscountAmount,
    });
    setModalUpdateOpen(true);
  };

  const columns = useMemo(() => [
    {
      name: 'InventoryId',
      selector: row => row.inventoryItem.objectId,
      sortable: true,
      cell: row => (
        <CustomLink
          text={row.inventoryItem.objectId}
          linkTo={`/inventory-items/${row.inventoryItem.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: row => row.inventoryItem.name,
      sortable: true,
    },
    {
      name: 'Discount Percent',
      selector: row => row.discountPercent,
      sortable: true,
    },
    {
      name: 'Fixed Discount Amount',
      selector: row => row.fixedDiscountAmount,
      sortable: true,
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      width: '100px',
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'d-flex ml-1'}>
          <div>
            <EditActionForTable
              onClick={() => editUserInventoryMappingFn(row)}
            />
          </div>
          <div className={'ml-4'}>
            <DeleteActionForTable
              onClick={() => deleteUserInventoryMappingFn(row.objectId)}
            />
          </div>
        </div>
      ),
    },

  ], []);

  if (loading) return (
    <div className={styles['loader-container']}>
      <Loading type={'mini'} />
    </div>
  );

  return (
    <div className={styles['user-inventory-container']}>
      {modalOpen && (
        <AddUserInventoryItemModal
          userId={userId}
          isUserPage={true}          
          selectedUserMapping={{}}
          onSuccess={fetchUsersInventoryFn}
          toggleModal={() => setModalOpen(!modalOpen)}
        />
      )}
      {modalUpdateOpen && (
        <AddUserInventoryItemModal
          isEdit={true}
          selectedItemToUpdate = {itemSelected}
          selectedUserMapping = {selectedUserMapping}
          onSuccess={fetchUsersInventoryFn}
          toggleModal={() => setModalUpdateOpen(!modalUpdateOpen)}
        />
      )}
      <div className={styles['section-header']}>
        <div className={styles['section-heading']}>Users Inventory</div>
        <ActionButton 
          active={true}
          isLoading={false}
          onClick={() => setModalOpen(true)}
          text={<>Inventory Item<span className={'ml-3'}>+</span></>}
        />
      </div>
      <DataTable
        columns={columns}
        noHeader={true}
        data={data}
        highlightOnHover={true}
        defaultSortField="createdAt"
        customStyles={tableStyles}
        keyField='objectId'
      />
    </div>
  );
};

export default UsersInventory;